import React, { useState, useEffect } from 'react';

// React DateTime
import moment from 'moment';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import Pagination from '../../../components/Footer/Pagination';

import { formatDate } from "../../../../utils/DateUtils";
import { getData, formatCurrency } from '../../../../utils/SamuiUtils';
import { getReport } from '../../../../utils/ReportUtils';
import { 
  useDataTableKeyboardNavigation, 
  getDataTableRowStyle, 
  getDataTableKeyboardInstructions 
} from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ name, dataMasterList, onPageInsert, onPageUpdate }) => {
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [docNo, setDocNo] = useState("");
  const [docType, setDocType] = useState("");
  const [arName, setArName] = useState("");
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(
    localStorage.getItem('currentPage') ? parseInt(localStorage.getItem('currentPage')) : 1
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') ? parseInt(localStorage.getItem('itemsPerPageDropdown')) : 10
  );
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);
  
  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });
  
  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // Initialize data
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      setCurrentPage(1);
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  // Filter when search term or dates change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0);
    filterItems();
    localStorage.setItem('searchTerm', searchTerm);
    localStorage.setItem('startDate', startDate);
    localStorage.setItem('endDate', endDate);
  }, [searchTerm, startDate, endDate, dataMasterList]);

  // Filter items function
  const filterItems = () => {
    try {
      let filtered = [...dataMasterList];

      // Date filtering
      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      filtered = filtered.filter((item) => {
        const itemDate = moment(item.Doc_Date, 'YYYY-MM-DD');
        return itemDate.isBetween(start, end, null, '[]');
      });

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          (item.Doc_No && item.Doc_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_Name && item.AR_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
    } catch (error) {
      console.error('Filter error:', error.message);
      setFilteredItems([]);
    }
  };
  
  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0);
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  
  const onRowSelected = async (fromData) => {
    if (fromData.Doc_Is_INV_From === 1) {
      const soData = await getData("API_0701_SO_H", `Doc_ID = ${fromData.Ref_DocID} ORDER BY Doc_No DESC`);
      if (soData && soData.length > 0) {
        setFilteredDetailList(soData);
      }
    } else if (fromData.Doc_Is_INV_From === 2) {
      const biData = await getData("API_1301_BL_H", `Doc_ID = ${fromData.Ref_DocID} ORDER BY Doc_No DESC`);
      if (biData && biData.length > 0) {
        setFilteredDetailList(biData);
      }
    } else {
      setFilteredDetailList([]);
    }

    setDocNo(fromData.Doc_No);
    setDocType(fromData.Doc_Is_INV_From === 1 ? "SO" : "BI");
    setArName(fromData.AR_Name);
  };

  // Field mappings for keyboard navigation
  const fieldMappings = {
    id: 'Doc_ID'
  };

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage,
    null, // getReport function
    {
      customKeyHandlers: {
        'Enter': (event, { selectedRowIndex, currentItems }) => {
          event.preventDefault();
          if (currentItems && currentItems[selectedRowIndex]) {
            onPageUpdate(currentItems[selectedRowIndex].Doc_No);
          }
        },
        ' ': (event, { selectedRowIndex, currentItems }) => {
          event.preventDefault();
          if (currentItems && currentItems[selectedRowIndex]) {
            onRowSelected(currentItems[selectedRowIndex]);
          }
        },
        'p': (event, { selectedRowIndex, currentItems }) => {
          event.preventDefault();
          if (currentItems && currentItems[selectedRowIndex]) {
            const selectedItem = currentItems[selectedRowIndex];
            const reportType = selectedItem.Doc_Is_INV_From === 1 ? "inv1_so" : "inv1_bi";
            getReport(reportType, selectedItem.Doc_No);
          }
        },
        'P': (event, { selectedRowIndex, currentItems }) => {
          event.preventDefault();
          if (currentItems && currentItems[selectedRowIndex]) {
            const selectedItem = currentItems[selectedRowIndex];
            const reportType = selectedItem.Doc_Is_INV_From === 1 ? "inv1_so" : "inv1_bi";
            getReport(reportType, selectedItem.Doc_No);
          }
        },
        'F5': (event) => {
          event.preventDefault();
          const searchInput = document.querySelector('input[type="text"]');
          if (searchInput) {
            searchInput.focus();
          }
        },
        'Escape': (event) => {
          event.preventDefault();
          setSearchTerm('');
        }
      }
    }
  );

  return <>
    <Breadcrumbs page={name} items={[
      { name: 'งานบัญชี', url: '/account' },
      { name: name, url: '/invoice' },
    ]} />
    <div className="container-fluid card py-3 hidden-scrollbar" style={{ maxHeight: "80vh", overflowY: "auto" }}>
      {/* Search and Filter Section */}
      <SearchAndFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="ค้นหาเลขเอกสาร, ลูกค้า..."
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        showDateFilter={true}
        onAddNew={onPageInsert}
        addButtonText={`สร้าง${name}`}
        showAddButton={true}
      />
      <hr />
      <div className="row ">
        <div className="col-5 mb-1" style={{ borderRight: '2px solid #c7c8c9' }}>
          <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
            <table id="basic-datatables" className="table table-striped table-hover">
              <thead className="thead-dark">
                <tr>
                  <th className="text-center" style={{ width: '20%' }}>วันที่</th>
                  <th className="text-center" style={{ width: '15%' }}>เลขเอกสาร</th>
                  <th className="text-center" style={{ width: '42%' }}>ชื่อลูกค้า</th>
                  <th className="text-center" style={{ width: '18%' }}>สร้างจาก</th>
                  <th className="text-center" style={{ width: '5%' }}>
                    <i className="fa fa-edit" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length > 0 ? (
                  currentItems.map((data, index) => (
                    <tr
                      key={data.Doc_Id || index + 1}
                      onClick={() => {
                        setSelectedRowIndex(index);
                        onRowSelected(data);
                      }}
                      style={getDataTableRowStyle(
                        index, 
                        selectedRowIndex, 
                        docNo, 
                        data, 
                        'Doc_No'
                      )}>
                      <td className="text-center">{formatDate(data.Doc_Date)}</td>
                      <td className="text-center">{data.Doc_No}</td>
                      <td className="text-left">{data.AR_Name || '-'}</td>
                      <td className="text-center">
                        <button
                          className="btn"
                          style={{
                            backgroundColor: data.Doc_Is_INV_From === 1 ? 'red' : 'orange',
                            color: 'white',
                            width: '100%'
                          }}
                        >
                          {data.Doc_Is_INV_From === 1 ? 'ใบขาย' : 'ใบวางบิล'}
                        </button>
                      </td>
                      <td className="text-center">
                        <button className="btn btn-sm" onClick={(e) => {
                          e.stopPropagation();
                          onPageUpdate(data.Doc_No);
                        }}>
                          <i className="fa fa-edit" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">
                      <center>
                        <h5>ไม่พบข้อมูล</h5>
                      </center>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination & Items per page selector */}
          <div className="bg-white mt-2" style={{ padding: '8px 0' }}>
            <div className="row m-0">
              <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                <div className="d-flex align-items-center flex-wrap">
                  <span className="me-2">แสดง:</span>
                  <select
                    className="form-select me-2"
                    style={{ width: 'auto', minWidth: '70px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = parseInt(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>รายการต่อหน้า | แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, filteredItems.length)} จาก {filteredItems.length} รายการ</span>
                </div>
                <div className="d-flex align-items-center flex-wrap">
                  <div className="text-muted me-3" style={{ fontSize: "12px" }}>
                    คีย์บอร์ด: ↑↓ (เลือกแถว) | Enter (แก้ไข) | Spacebar (ดูรายละเอียด) | P (ดาวน์โหลดรายงาน) | ←→ (เปลี่ยนหน้า) | F5 (ค้นหา)
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    dataMasterList={filteredItems}
                    handlePageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-7 mb-3">
          {filteredDetailList.length !== 0 && (
            <div className="justify-content-between align-items-center mb-3">
              <div className="row">
                <div className="col-12">
                  <p className="card-title mb-0">
                    เอกสารเลขที่: {docNo} {arName}
                  </p>
                </div>
                <div className="col-12 d-flex justify-content-end">
                  <button
                    className="btn d-flex align-items-center text-white me-2"
                    style={{
                      backgroundColor: 'rgb(239, 108, 0)',
                      fontSize: '16px',
                    }}
                    onClick={() => getReport(docType === "SO" ? "inv1_so" : "inv1_bi", docNo)}
                  >
                    <i className="fa fa-print me-2" aria-hidden="true"></i>
                    พิมพ์ใบกำกับภาษี (ต้นฉบับ)
                  </button>
                  <button
                    className="btn d-flex align-items-center text-white"
                    style={{
                      backgroundColor: 'rgb(239, 108, 0)',
                      fontSize: '16px',
                    }}
                    onClick={() => getReport(docType === "SO" ? "inv2_so" : "inv2_bi", docNo)}
                  >
                    <i className="fa fa-print me-2" aria-hidden="true"></i>
                    พิมพ์ใบกำกับภาษี (สำเนา)
                  </button>
                </div>
              </div>
            </div>
          )}
          <div
            className="table-responsive"
            style={{
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            <table id="basic-datatables" className="table table-striped table-hover" style={{ minWidth: '100%' }}>
              <thead className="thead-dark">
                <tr>
                  <th className="text-center" style={{ width: '3%' }}>ลำดับ</th>
                  <th className="text-center" style={{ width: '14%' }}>เลขที่{docType === 'SO' ? 'ใบขาย' : 'ใบวางบิล'}</th>
                  <th className="text-center" style={{ width: '10%' }}>วันที่เอกสาร</th>
                  <th className="text-center" style={{ width: '40%' }}>ชื่อลูกค้า</th>
                  <th className="text-center" style={{ width: '20%' }}>รวมราคา</th>
                  <th className="text-center" style={{ width: '22%' }}>สถานะ{docType === 'SO' ? 'ใบขาย' : 'ใบวางบิล'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDetailList.length > 0 ? (
                  filteredDetailList.map((item, index) => (
                    <tr key={item.DT_Id || index + 1}>
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">{item.Doc_No}</td>
                      <td className="text-center">{formatDate(item.Doc_Date)}</td>
                      <td className="text-left">{item.AR_Name}</td>
                      <td className="text-end">{docType === 'SO' ? formatCurrency(item.NetTotal) : formatCurrency(item.BL_Net_Total)}</td>
                      <td className="text-center">
                        <button
                          className="btn"
                          style={{
                            backgroundColor: docType === 'SO' ? item.DocStatusPaid_Colour : item.DocBLStatusPaid_Colour,
                            color: 'white',
                            width: '100%'
                          }}
                        >
                          {docType === 'SO' ? item.DocStatusPaid_Name : item.DocBLStatusPaid_Name}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <center>
                        <h5>ไม่พบข้อมูล</h5>
                      </center>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </>;
};

export default Main;